import { PlateView } from "../plate_viewer"
import * as PIXI from "pixi.js"
import { PlatformTag, detectPlatform, KEYS } from "../app/providers/device_context"
import { view_to_grid, view_to_plane } from "./coords"

type SpotInfo = {
  vx: number
  vy: number
  sx: number
  sy: number
  px: number
  py: number
  gx: number
  gy: number
  dvx: number
  dvy: number
  dgx: number
  dgy: number
  dpx: number
  dpy: number
}

type PointerDownInfo = {
  lassoing: boolean
  rightClick: boolean
  start: SpotInfo
}

type LongClickInfo = {
  start: SpotInfo
}

type PointerMoveInfo = {
  lassoing: boolean
  rightClick: boolean
  start: SpotInfo
  current: SpotInfo
}

const IDEA_DRAG_THRESHOLD = 500

class PointerDownHandler {
  data: {
    time: number
    rightClick: boolean
    start: SpotInfo
  }
  plateView: PlateView
  longClickDetectTimer: number | null = null
  cursorLocation: { x: number; y: number } = { x: 0, y: 0 }
  platform: PlatformTag

  constructor(
    e: PIXI.FederatedPointerEvent,
    { x: sx, y: sy }: { x: number; y: number },
    plateView: PlateView,
    private onLongClick: (data: LongClickInfo) => void,
  ) {
    const { x: vx, y: vy } = e.data.global
    const view_location = { vx, vy }
    const camera_state = plateView.camera_state()

    this.data = {
      time: Date.now(),
      rightClick: e.data.nativeEvent.which === 3,
      start: {
        vx,
        vy,
        sx,
        sy,
        ...view_to_grid(view_location, camera_state),
        ...view_to_plane(view_location, camera_state),
        dgx: 0,
        dgy: 0,
        dpx: 0,
        dpy: 0,
        dvx: 0,
        dvy: 0,
      },
    }
    this.plateView = plateView
    this.platform = detectPlatform()
  }

  pointerDown(e: PIXI.FederatedPointerEvent, callback: (x: PointerDownInfo) => void) {
    this.cursorLocation = e.data.global
    callback({
      lassoing: this.isLassoing(e),
      rightClick: this.data.rightClick,
      start: this.data.start,
    })
    this.longClickDetectTimer = window.setTimeout(() => {
      if (this.data.rightClick) return false
      const result =
        Math.abs(this.cursorLocation.x - this.data.start.vx) < 10 &&
        Math.abs(this.cursorLocation.y - this.data.start.vy) < 10
      if (result) {
        this.onLongClick({ start: this.data.start })
      }
    }, IDEA_DRAG_THRESHOLD)
  }

  pointerMove(e: PIXI.FederatedPointerEvent, callback: (x: PointerMoveInfo) => void) {
    this.cursorLocation = e.data.global
    callback({
      ...this.info(e),
    })
  }

  pointerUp(e: PIXI.FederatedPointerEvent, callback: Function) {
    if (this.longClickDetectTimer) clearTimeout(this.longClickDetectTimer)
    const { x: vx, y: vy } = e.data.global
    const isQuickClick = Date.now() - this.data.time < 300
    const isLongClick = Date.now() - this.data.time > 1000
    const nearClick =
      Math.abs(this.cursorLocation.x - this.data.start.vx) < 10 &&
      Math.abs(this.cursorLocation.y - this.data.start.vy) < 10
    callback({
      isClick: isQuickClick && nearClick && !this.data.rightClick,
      isQuickClick,
      isLongClick,
      ...this.info(e),
    })
  }

  info(e: PIXI.FederatedPointerEvent) {
    const { x: vx, y: vy } = e.data.global
    const view_location = { vx, vy }
    const camera_state = this.plateView.camera_state()
    const { gx, gy } = view_to_grid(view_location, camera_state)
    const { px, py } = view_to_plane(view_location, camera_state)
    const start = this.data.start
    return {
      lassoing: this.isLassoing(e),
      rightClick: this.data.rightClick,

      start: this.data.start,
      current: {
        vx,
        vy,
        dvx: vx - start.vx,
        dvy: vy - start.vy,
        px,
        py,
        gx,
        gy,
        dgx: gx - start.gx,
        dgy: gy - start.gy,
        dpx: (vx - start.vx) / this.plateView.scale,
        dpy: (vy - start.vy) / this.plateView.scale,
        sx: 0,
        sy: 0,
      },
    }
  }

  isLassoing(e) {
    return e[KEYS.lassoingEventKey[this.platform]]
  }
}

export default PointerDownHandler
